import { UseQueryResult } from "@tanstack/react-query";
import { School, User } from "lucide-react";
import { ReactNode } from "react";
import { UseFormReturn } from "react-hook-form";

import {
  AlertRow,
  AlertsMasterToggle,
  CadencePicker,
} from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/alerts/alert-components.view";
import { JobAlertsFormValues } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/alerts/job-alerts-form";
import {
  frequencyForToggle,
  isActiveFrequency,
} from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/alerts/notify-frequency";
import {
  SavedSearchAlertRow,
  SavedSearchesSection,
} from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/alerts/saved-searches-section";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";

const SAVED_SCHOOLS_SUBTITLE = "New jobs at any school you've saved";

export interface AlertsModalViewProps {
  open: boolean;
  onClose: () => void;
  form: UseFormReturn<JobAlertsFormValues>;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  description: ReactNode;
  preferencesSubtitle: string;
  savedSearches: SavedSearchAlertRow[];
  savedSearchesQueries: UseQueryResult[];
  isSaving: boolean;
}

export const AlertsModalView = ({
  open,
  onClose,
  form,
  onSubmit,
  description,
  preferencesSubtitle,
  savedSearches,
  savedSearchesQueries,
  isSaving,
}: AlertsModalViewProps) => {
  const isEnabled = isActiveFrequency(form.watch("notifyFrequency"));
  const submitError = form.formState.errors.root;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Job alerts</DialogTitle>
        </DialogHeader>
        {description}

        <Form {...form}>
          <form onSubmit={onSubmit}>
            <FormField
              control={form.control}
              name="notifyFrequency"
              render={({ field }) => (
                <>
                  <FormItem>
                    <FormControl>
                      <AlertsMasterToggle
                        checked={isActiveFrequency(field.value)}
                        onCheckedChange={(checked) =>
                          field.onChange(
                            frequencyForToggle(checked, field.value),
                          )
                        }
                      />
                    </FormControl>
                  </FormItem>
                  <FormItem>
                    <FormControl>
                      <CadencePicker
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={!isActiveFrequency(field.value)}
                      />
                    </FormControl>
                  </FormItem>
                </>
              )}
            />

            <div className="border-t border-surface-subtle mt-3 pt-3 px-1">
              <p className="text-[11px] font-semibold tracking-wider text-ink-subtle uppercase mb-1">
                What to include
              </p>
            </div>

            <div>
              <FormField
                control={form.control}
                name="preferencesEnabled"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <AlertRow
                        title="Your preferences"
                        checked={field.value}
                        enabled={isEnabled}
                        subtitle={preferencesSubtitle}
                        onChange={field.onChange}
                        icon={<User className="size-4" />}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="savedSchoolsEnabled"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <AlertRow
                        title="Saved schools"
                        checked={field.value}
                        enabled={isEnabled}
                        subtitle={SAVED_SCHOOLS_SUBTITLE}
                        onChange={field.onChange}
                        icon={<School className="size-4" />}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="savedSearchesEnabled"
              render={({ field }) => (
                <FormItem>
                  <SavedSearchesSection
                    savedSearches={savedSearches}
                    queries={savedSearchesQueries}
                    checked={field.value}
                    enabled={isEnabled}
                    onChange={field.onChange}
                  />
                </FormItem>
              )}
            />

            {submitError && (
              <p
                className="pt-4 text-xs text-destructive"
                role="alert"
                data-testid="job-alert-submit-error"
              >
                {submitError.message}
              </p>
            )}

            <DialogFooter className="pt-5">
              <Button
                type="submit"
                disabled={isSaving}
                className="bg-surface-inverted text-primary-foreground hover:bg-surface-inverted/90"
                data-testid="job-alert-confirm"
              >
                Done
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
